import React, { useState, useCallback, useRef } from 'react';
import { ArrowNarrowUpIcon } from '@heroicons/react/outline';
import { useImageInput } from '@hooks/files/useImageInput';
import { useImageModalContext } from './ImageSelectModal';
import { AddImageSVG } from '@components/common/svgs/AddImageSVG';
import { useWindowEvent } from '@hooks/useWindowEvent';
import cs from 'classnames';
import { Text } from '@skipper/ui';
import {
    AnimatedImageCropper,
    ImageModalFooter,
    ImageModalHeader,
    MultiFileInputView,
    SaveAndSubmit,
    ShowSingleFileInput,
} from './SharedComponents';
import { filterByFileTypes, useFileTypeImage } from '@components/file/fileUtilities';

export function UploadImage({ toggle }: { toggle: any }) {
    const {
        fileCreate,
        authorizer,
        fileList,
        getContentMimeTypes,
        isImmediateUpdate,
        isMultiFile,
        allowedFileTypes,
    } = useImageModalContext();
    const { fileInput, crop, handlers } = useImageInput({
        immediateUpdate: isImmediateUpdate,
        updateNow: (file) => {
            fileCreate.mutate({
                title: file.name ?? `file-${Date.now()}`,
                file: file,
                authorizerItem: authorizer,
            });
        },
        multiple: isMultiFile,
        fileTypeFilter: filterByFileTypes(allowedFileTypes),
    });

    const selectIcon = useFileTypeImage();

    let showFileInputSingle = false;
    let isCroppable = false;
    let fileType: string;
    if (fileInput?.file?.type) {
        fileType = fileInput?.file?.type;
        isCroppable = fileType?.match(/((png|jpeg|png|jpg)$)/gi)?.length > 0;
        if (!isMultiFile) {
            showFileInputSingle = true;
        }
    }
    isCroppable &&= !isImmediateUpdate; // override for immediate update
    return (
        <div className="relative flex flex-col flex-1 overflow-hidden">
            <div>
                <ImageModalHeader onClick={() => toggle(false)} text="Upload from Computer" />
            </div>
            {showFileInputSingle && (
                <div className="grid grid-cols-1 w-full h-full">
                    {isCroppable && (
                        <div className="col-span-1 flex flex-1 items-center justify-center pt-6">
                            <AnimatedImageCropper
                                selected={{ url: fileInput?.localUrl || '', ...fileInput }}
                                handlers={handlers}
                                crop={crop}
                                close={close}
                                onClickHeader={() => toggle(false)}
                            />
                        </div>
                    )}
                    {!isCroppable && (
                        <React.Fragment>
                            <div className="col-span-1 flex flex-1 items-center justify-center pt-6">
                                <ShowSingleFileInput
                                    fileInput={fileInput}
                                    selectIcon={selectIcon}
                                />
                            </div>
                            {!isImmediateUpdate && (
                                <div className="col-span-1 flex items-center justify-center pt-6">
                                    <SaveAndSubmit fileInput={fileInput} submitButtonText="Save" />
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            )}

            {!showFileInputSingle && (
                <ImageDropZoneArea
                    handlers={handlers}
                    multiple={isMultiFile}
                    disabled={isMultiFile && fileList?.length === 5}
                    getContentMimeTypes={getContentMimeTypes}
                    dropZoneText={
                        allowedFileTypes === 'avatar' ? 'Select JPG or PNG to upload' : undefined
                    }
                >
                    {!!fileList?.length && (
                        <MultiFileInputView
                            fileList={fileList}
                            fileCreate={fileCreate}
                            selectIcon={selectIcon}
                        />
                    )}
                </ImageDropZoneArea>
            )}
            <ImageModalFooter />
        </div>
    );
}

function ImageDropZoneArea({
    handlers,
    multiple,
    disabled,
    getContentMimeTypes,
    children,
    dropZoneText = 'Select files to upload',
}: {
    handlers: ReturnType<typeof useImageInput>['handlers'];
    multiple?: boolean;
    disabled?: boolean;
    getContentMimeTypes: ReturnType<typeof useImageModalContext>['getContentMimeTypes'];
    children: React.ReactNode;
    dropZoneText?: string;
}) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dragContainer = useRef<HTMLDivElement | null>(null);
    const [dragEvents, setDragEvents] = useState(0);
    const isDragging = useRef(false);
    const onDragStart = useCallback((e: React.DragEvent) => {
        if (e.dataTransfer.types) {
            for (let i = 0; i < e.dataTransfer.types.length; i++) {
                const f = e.dataTransfer.types[i] === 'Files';
                if (f && dragContainer?.current?.contains(e?.target as Node)) {
                    setDragEvents((n) => n + 1);
                    isDragging.current = true;
                }
            }
        }
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent) => {
        if (e.dataTransfer.types) {
            for (let i = 0; i < e.dataTransfer.types.length; i++) {
                const f = e.dataTransfer.types[i] === 'Files';
                if (f && dragContainer?.current?.contains(e?.target as Node)) {
                    setDragEvents((n) => Math.max(n - 1, 0));
                    isDragging.current = true;
                }
            }
        }
    }, []);

    const isFileDrag = dragEvents > 0;

    useWindowEvent('drop', (ev) => {
        ev.preventDefault();
        setDragEvents((n) => Math.max(n - 1, 0));
    });
    useWindowEvent('dragleave', (ev) => {
        ev.preventDefault();
        if (dragContainer.current?.contains(ev.target as Node)) {
            setDragEvents((n) => Math.max(n - 1, 0));
        }
    });

    const classNames = cs(
        'z-50 border-4 p-10 flex flex-col items-center justify-center mb-8 rounded-sm w-full cursor-pointer hover:text-primary text-gray-500',
        {
            ['border-dashed border-gray-300']: !isFileDrag,
            ['border border-teal-600 ring-2 ring-teal-300']: isFileDrag,
        },
    );

    const accept = (getContentMimeTypes('dropbox') as string[])?.join(', ');

    return (
        <div
            ref={dragContainer}
            className="relative w-full h-full flex-col justify-center items-center grid grid-cols-8"
            onDragEnter={onDragStart}
            onDragLeave={onDragLeave}
        >
            {children}
            <div className="col-span-6 col-start-2">
                <div className={classNames}>
                    <div className="">
                        {isFileDrag && !disabled ? <UploadArrow /> : <AddImageSVG />}
                    </div>
                    <div className="flex items-center flex-col justify-center cursor-pointer ">
                        {disabled ? (
                            <Text size="small">Max of 5 files for this upload reached</Text>
                        ) : (
                            <React.Fragment>
                                <p className="pointer-events-none cursor-pointer text-sm">
                                    {dropZoneText}
                                </p>
                            </React.Fragment>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={inputRef}
                        onDrop={handlers.fileDropEventHandler}
                        onChange={handlers.fileInputEventHandler}
                        multiple={multiple}
                        disabled={disabled}
                        accept={accept}
                        className="absolute inset-0 opacity-0 z-1001 w-full h-full cursor-pointer"
                    />
                </div>
            </div>
        </div>
    );
}

function UploadArrow() {
    return (
        <div className="p-3 rounded-full bg-teal-600 ring-2 ring-teal-300">
            <ArrowNarrowUpIcon className="w-6 h-6 text-white" />
        </div>
    );
}
